
import { defineComponent } from "vue";
import { TableEditingConfig } from "@/components/controls/catalogueGrid/tableEditingConfig";
import { TableSelectionConfig } from "@/components/controls/catalogueGrid/tableSelectionConfig";
import { TableDesignConfig } from "@/components/controls/catalogueGrid/tableDesignConfig";
import { TextColumnConfig } from "@/components/controls/catalogueGrid/columns/textColumnConfig";
import { IntegerColumnConfig } from "@/components/controls/catalogueGrid/columns/integerColumnConfig";
import { requiredRule } from "@/components/controls/catalogueGrid/columns/baseValidationRules";
import CatalogueGrid from "@/components/controls/catalogueGrid/CatalogueGrid.vue";
import { BooleanColumnConfig } from "@/components/controls/catalogueGrid/columns/booleanColumnConfig";
import { DecimalColumnConfig } from "@/components/controls/catalogueGrid/columns/decimalColumnConfig";
import { CatalogueColumnConfig } from "@/components/controls/catalogueGrid/columns/catalogueColumnConfig";
import planContainerCreateEditDialog from "./PlanContainerCreateEditDialog.vue";
import { TableExportConfig } from "@/components/controls/catalogueGrid/tableExportConfig";
import { TableImportConfig } from "@/components/controls/catalogueGrid/tableImportConfig";
import store from "@/store";

export default defineComponent({
  components: {
    CatalogueGrid,
  },
  props: {
    //показатель режима выбора
    selectMode: { required: false, type: Boolean, default: false },
    gridHeight: { required: false, type: String, default: null },
    //функция для получения выбранных элементов
    getSelectedData: { type: Function },
    //функция для вызова при изменении выбранных элементов
    changeSelectedData: { type: Function },
    //модель родительского элемента
    parentModel: { required: true },
    //идет расчет плана
    isCalculation: { required: false },
    //открыты ли настройки
    isSettingsOpen: { required: true },
  },
  computed: {
    planContainerCreateEditDialog(): any {
      return planContainerCreateEditDialog;
    },
  },
  methods: {
    getScrollSettings() {
      const containerSettings = (store as any).state.containerSettings
        .containerSettings[this.parentModel.id];
      if (containerSettings) {
        this.designConfig.horizontalScroll = containerSettings.scroll;
      }
    },
    rememberScrollSettings(scroll: any) {
      const temp = {
        settings: {
          scroll: scroll,
        },
        index: this.parentModel.id,
      };
      store.dispatch("containerSettings/setContainerSettings", temp);
    },
  },
  data() {
    return {
      // подробности работы custom summary см. на сервере в методе SetCustomSummary
      summaryConfig: {
        totalItems: [
          {
            name: "TotalSummary",
            showInColumn: "containerDescription",
            displayFormat: "Итог:",
            summaryType: "custom",
          },
          {
            name: "CountSummary",
            showInColumn: "count",
            column: "count",
            valueFormat: "#,##0.#",
            displayFormat: "{0} шт.",
            summaryType: "sum",
          },

          {
            name: "LoadCapacitySummary",
            showInColumn: "loadCapacity",
            column: "loadCapacity",
            valueFormat: "#,##0.#",
            displayFormat: "{0} т.",
            summaryType: "sum",
          },

          {
            name: "TotalLoadCapacitySummary",
            showInColumn: "totalLoadCapacity",
            column: "totalLoadCapacity",
            valueFormat: "#,##0.#",
            displayFormat: "{0} т.",
            summaryType: "sum",
          },
        ],
      },
      editingConfig: new TableEditingConfig({
        allowUpdating: !this.isCalculation,
        allowAdding: !this.isCalculation,
        allowDeleting: !this.isCalculation,
        confirmDelete: true,
        allowDataFill: !this.isCalculation,
        allowExport: true,
        allowImport: !this.isCalculation,
        allowDeleteAll: !this.isCalculation,
        allowClearFilter: true,
        allowDeleteMultipleRows: true,
        allowChangeScrollSettings: true,
        allowColumnReordering: true,
        allowResetSelectedRows: true,
        chooseColumns: true,
      }),
      selectModeEditingConfig: new TableEditingConfig({
        allowUpdating: false,
        allowAdding: false,
        allowDeleting: false,
        confirmDelete: false,
        allowDataFill: false,
        allowExport: false,
        allowDeleteMultipleRows: false,
      }),
      designConfig: new TableDesignConfig({
        popUpColumnsCount: 1,
        popUpWidth: "850px",
        heightScroll: "calc(100vh - 350px)",
        horizontalScroll: true,
      }),
      columns: [
        new CatalogueColumnConfig({
          dataField: "containerDescription",
          caption: "Контейнер",
          modelIdField: "containerId",
          modelDescField: "containerDescription",
          keyField: "id",
          displayField: "containerDescription",
          displayDescField: "name",
          catalogueComponentName:
            "catalogues/ContainerCatalogue/ContainerCatalogue",
          descriptionColumnName: "containerDescription",
        }),

        new TextColumnConfig({
          dataField: "name",
          caption: "Наименование",
          validationRules: [requiredRule],
        }),

        new TextColumnConfig({
          dataField: "code",
          caption: "Код контейнера",
        }),

        new IntegerColumnConfig({
          dataField: "length",
          caption: "Длина (мм)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new IntegerColumnConfig({
          dataField: "width",
          caption: "Ширина (мм)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new IntegerColumnConfig({
          dataField: "height",
          caption: "Высота (мм)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "loadCapacity",
          caption: "Грузоподъемность (кг)",
          hideFraction: true,
          decimalPoints: 0,
          min: 0,
          step: 1,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "emptyWeight",
          caption: "Вес порожнего (кг)",
          hideFraction: true,
          decimalPoints: 0,
          min: 0,
          step: 1,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "requisiteWeight",
          caption: "Вес реквизита (кг)",
          hideFraction: true,
          decimalPoints: 0,
          min: 0,
          step: 1,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "loadStatThreshold",
          caption: "Рек. порог статнагрузки (м³)",
          decimalPoints: 0,
          min: 0,
          step: 1,
          validationRules: [requiredRule],
        }),

        new IntegerColumnConfig({
          dataField: "emptyHeight",
          caption: "Высота порожнего (мм)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new CatalogueColumnConfig({
          dataField: "gapDescription",
          caption: "Отступы (мм)",
          modelIdField: "gapId",
          modelDescField: "gapDescription",
          keyField: "id",
          displayField: "gapDescription",
          displayDescField: "name",
          catalogueComponentName: "catalogues/GapCatalogue/GapCatalogue",
          descriptionColumnName: "gapDescription",
          validationRules: [requiredRule],
        }),

        new CatalogueColumnConfig({
          dataField: "displacementDescription",
          caption: "Смещение",
          modelIdField: "displacementId",
          modelDescField: "displacementDescription",
          keyField: "id",
          displayField: "displacementDescription",
          displayDescField: "name",
          catalogueComponentName:
            "catalogues/DisplacementCatalogue/DisplacementCatalogue",
          descriptionColumnName: "displacementDescription",
          validationRules: [requiredRule],
        }),

        new IntegerColumnConfig({
          dataField: "count",
          caption: "Кол-во (шт)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "totalLoadCapacity",
          caption: "Общая грузоподъемность (т)",
          decimalPoints: 1,
          readOnly: true,
        }),

        new BooleanColumnConfig({
          dataField: "isSelectedForPlanning",
          caption: "Выбран для планирования",
          //disabled: true,
        }),
      ],
      importConfig: new TableImportConfig({
        progressMethodName: "importPlanContainerProgress",
        progressEventName: "importPlanContainerProgressEvent",
        messageEventName: "importPlanContainerMessageEvent",
      }),
      exportConfig: new TableExportConfig({
        progressMethodName: "exportPlanContainerProgress",
        progressEventName: "exportPlanContainerProgressEvent",
        messageEventName: "exportPlanContainerMessageEvent",
        exportFileName: "Контейнеры-Opti-Loading",
      }),
      selectionConfig: new TableSelectionConfig({
        mode: "multiple",
        selectAllMode: "allPages",
        showCheckBoxesMode: "none",
      }),
      pageSizes: [10, 20, 50, 100],
    };
  },
  created() {
    this.getScrollSettings();
    if (this.selectMode) {
      this.editingConfig = this.selectModeEditingConfig;
    }
    if (this.gridHeight) {
      this.designConfig.height = this.gridHeight;
    }
  },
});
